import React, { useState, useEffect } from 'react';
import { Save, AlertTriangle, Info, Shield } from 'lucide-react';
import apiClient from '../../services/api';

interface TaxSettingsData {
  standard_rate: number;
  reduced_rate: number;
  tax_mode: 'internal' | 'external' | 'exempt';
  is_tax_exempt: boolean;
}

const TaxSettings: React.FC = () => {
  const [settings, setSettings] = useState<TaxSettingsData>({
    standard_rate: 10,
    reduced_rate: 8,
    tax_mode: 'internal',
    is_tax_exempt: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await apiClient.get('/tax-settings');
      setSettings(response.data);
    } catch (error) {
      console.error('税制設定の取得に失敗:', error);
      setMessage('設定の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');

    try {
      await apiClient.put('/tax-settings', settings);
      setMessage('設定を保存しました');

      // 成功メッセージを3秒後に消す
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('保存エラー:', error);
      setMessage('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleTaxModeChange = (mode: 'internal' | 'external' | 'exempt') => {
    setSettings(prev => ({
      ...prev,
      tax_mode: mode,
      is_tax_exempt: mode === 'exempt'
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-500">読み込み中...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg">
        {/* ヘッダー */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-lg">
          <h1 className="text-2xl font-bold flex items-center">
            <Shield className="mr-3" />
            税制設定
          </h1>
          <p className="mt-2 text-blue-100">
            消費税率の設定と免税事業者モードの管理
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* 税制モード選択 */}
          <div className="border-b pb-6">
            <h2 className="text-lg font-bold mb-4">税制モード</h2>
            <div className="grid grid-cols-3 gap-4">
              <button
                onClick={() => handleTaxModeChange('internal')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  settings.tax_mode === 'internal'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="font-bold mb-1">内税</div>
                <div className="text-sm text-gray-600">
                  価格に税金を含む
                </div>
              </button>

              <button
                onClick={() => handleTaxModeChange('external')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  settings.tax_mode === 'external'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="font-bold mb-1">外税</div>
                <div className="text-sm text-gray-600">
                  価格に税金を加算
                </div>
              </button>

              <button
                onClick={() => handleTaxModeChange('exempt')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  settings.tax_mode === 'exempt'
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="font-bold mb-1 flex items-center justify-center">
                  免税事業者
                  <Shield className="w-4 h-4 ml-1" />
                </div>
                <div className="text-sm text-gray-600">
                  消費税なし
                </div>
              </button>
            </div>

            {settings.tax_mode === 'exempt' && (
              <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5 mr-2" />
                  <div className="text-sm">
                    <div className="font-bold text-orange-800 mb-1">
                      免税事業者モード
                    </div>
                    <div className="text-orange-700">
                      レシートに「免税」と表示されます。
                      売上記録には税額が記録されますが、請求はされません。
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 税率設定 */}
          <div className="space-y-4">
            <h2 className="text-lg font-bold">税率設定</h2>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  標準税率（店内飲食）
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={settings.standard_rate}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      standard_rate: parseFloat(e.target.value) || 0
                    }))}
                    disabled={settings.tax_mode === 'exempt'}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                  <span className="ml-2 text-lg font-semibold">%</span>
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  現在の設定: {settings.standard_rate}%
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  軽減税率（テイクアウト）
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={settings.reduced_rate}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      reduced_rate: parseFloat(e.target.value) || 0
                    }))}
                    disabled={settings.tax_mode === 'exempt'}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                  <span className="ml-2 text-lg font-semibold">%</span>
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  現在の設定: {settings.reduced_rate}%
                </div>
              </div>
            </div>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <Info className="w-5 h-5 text-blue-600 mt-0.5 mr-2" />
                <div className="text-sm text-blue-800">
                  <div className="font-bold mb-1">税率の適用ルール</div>
                  <ul className="space-y-1">
                    <li>• 「弁当」「テイクアウト」カテゴリ → 軽減税率（{settings.reduced_rate}%）</li>
                    <li>• 「定食」「サイドメニュー」「ドリンク」 → 標準税率（{settings.standard_rate}%）</li>
                    <li>• 個別商品で上書き設定が可能</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* 計算例 */}
          <div className="border-t pt-6">
            <h2 className="text-lg font-bold mb-4">計算例（1,000円の商品）</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="font-medium mb-2">店内飲食（{settings.standard_rate}%）</div>
                {settings.tax_mode === 'internal' && (
                  <div className="text-sm space-y-1">
                    <div>税込価格: ¥1,000</div>
                    <div>内税額: ¥{Math.floor(1000 - (1000 / (1 + settings.standard_rate / 100)))}</div>
                    <div>税抜価格: ¥{Math.floor(1000 / (1 + settings.standard_rate / 100))}</div>
                  </div>
                )}
                {settings.tax_mode === 'external' && (
                  <div className="text-sm space-y-1">
                    <div>税抜価格: ¥1,000</div>
                    <div>消費税: ¥{Math.floor(1000 * settings.standard_rate / 100)}</div>
                    <div>税込価格: ¥{1000 + Math.floor(1000 * settings.standard_rate / 100)}</div>
                  </div>
                )}
                {settings.tax_mode === 'exempt' && (
                  <div className="text-sm space-y-1">
                    <div>販売価格: ¥1,000</div>
                    <div className="text-orange-600">（免税）</div>
                  </div>
                )}
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="font-medium mb-2">テイクアウト（{settings.reduced_rate}%）</div>
                {settings.tax_mode === 'internal' && (
                  <div className="text-sm space-y-1">
                    <div>税込価格: ¥1,000</div>
                    <div>内税額: ¥{Math.floor(1000 - (1000 / (1 + settings.reduced_rate / 100)))}</div>
                    <div>税抜価格: ¥{Math.floor(1000 / (1 + settings.reduced_rate / 100))}</div>
                  </div>
                )}
                {settings.tax_mode === 'external' && (
                  <div className="text-sm space-y-1">
                    <div>税抜価格: ¥1,000</div>
                    <div>消費税: ¥{Math.floor(1000 * settings.reduced_rate / 100)}</div>
                    <div>税込価格: ¥{1000 + Math.floor(1000 * settings.reduced_rate / 100)}</div>
                  </div>
                )}
                {settings.tax_mode === 'exempt' && (
                  <div className="text-sm space-y-1">
                    <div>販売価格: ¥1,000</div>
                    <div className="text-orange-600">（免税）</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* メッセージ表示 */}
          {message && (
            <div className={`p-4 rounded-lg ${
              message.includes('失敗')
                ? 'bg-red-50 text-red-700 border border-red-200'
                : 'bg-green-50 text-green-700 border border-green-200'
            }`}>
              {message}
            </div>
          )}

          {/* 保存ボタン */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 active:scale-95 disabled:bg-gray-400 transition-all flex items-center"
            >
              <Save className="mr-2" />
              {saving ? '保存中...' : '設定を保存'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxSettings;